import React, { useState, useMemo } from 'react';
import { 
  Search, 
  Filter,
  RefreshCw,
  Loader2,
  Users,
  Plus,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
  Eye
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import BookingDetailDrawer from './BookingDetailDrawer';
import ManualWeekenderBookingDialog from './ManualWeekenderBookingDialog';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  deposit_paid: 'bg-blue-100 text-blue-700',
  fully_paid: 'bg-emerald-100 text-emerald-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-slate-100 text-slate-600'
};

const statusLabels = {
  pending: 'Pending',
  deposit_paid: 'Deposit Paid',
  fully_paid: 'Fully Paid',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  refunded: 'Refunded'
};

const formatCurrency = (amount) => {
  return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Calculate guest count from booking
const getGuestCount = (booking) => {
  // Use authoritative field first
  if (booking.number_of_guests) return booking.number_of_guests;
  if (booking.total_guests) return booking.total_guests;
  if (booking.guests?.length) return booking.guests.length;
  return 1;
};

// Get primary room type - handles ARRAY format from API: [{room_type, quantity}]
const getPrimaryRoom = (booking) => {
  if (!booking.rooms) return '-';
  
  // Handle array format (authoritative API response)
  if (Array.isArray(booking.rooms)) {
    const validRooms = booking.rooms.filter(r => r.quantity > 0);
    if (validRooms.length === 0) return '-';
    if (validRooms.length === 1) {
      const r = validRooms[0];
      return `${r.quantity}x ${r.room_type}`;
    }
    return `${validRooms.length} room types`;
  }
  
  // Fallback for object format (legacy)
  const entries = Object.entries(booking.rooms).filter(([_, qty]) => qty > 0);
  if (entries.length === 0) return '-';
  if (entries.length === 1) return `${entries[0][1]}x ${entries[0][0]}`;
  return `${entries.length} room types`;
};

// Calculate balance
const getBalance = (booking) => {
  const total = booking.total_override || booking.total_amount || booking.total_price || 0;
  const paid = booking.amount_paid || 0;
  return Math.max(0, total - paid);
};

export default function WeekenderBookings({ 
  event, 
  bookings, 
  loading, 
  authToken,
  onBookingChange,
  onRefresh 
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);

  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.booking_id?.toLowerCase().includes(query) ||
        b.customer_name?.toLowerCase().includes(query) ||
        b.customer_email?.toLowerCase().includes(query) ||
        b.customer_phone?.includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'customer_name':
          aVal = a.customer_name?.toLowerCase() || '';
          bVal = b.customer_name?.toLowerCase() || '';
          break;
        case 'amount_paid':
          aVal = a.amount_paid || 0;
          bVal = b.amount_paid || 0;
          break;
        case 'balance':
          aVal = getBalance(a);
          bVal = getBalance(b);
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [bookings, searchQuery, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const openBookingDrawer = (booking) => {
    setSelectedBooking(booking);
    setDrawerOpen(true);
  };

  const closeBookingDrawer = () => {
    setDrawerOpen(false);
    setSelectedBooking(null);
  };

  const handleBookingUpdated = () => {
    onBookingChange();
    // Refresh the selected booking
    if (selectedBooking) {
      const updated = bookings.find(b => b.booking_id === selectedBooking.booking_id);
      if (updated) setSelectedBooking(updated);
    }
  };

  // Get unique statuses for filter
  const statuses = useMemo(() => {
    const unique = new Set(bookings.map(b => b.status));
    return Array.from(unique).filter(Boolean);
  }, [bookings]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Bookings</h2>
          <p className="text-sm text-slate-500">
            {filteredBookings.length} of {bookings.length} bookings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
          {/* Manual Booking Button */}
          <Button 
            size="sm" 
            className="bg-violet-600 hover:bg-violet-700"
            onClick={() => setManualBookingOpen(true)}
            data-testid="add-booking-button"
          >
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Add Booking</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, booking ref..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              <Filter className="w-4 h-4 mr-2" />
              {statusFilter === 'all' ? 'All Statuses' : statusLabels[statusFilter]}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setStatusFilter('all')}>
              All Statuses
            </DropdownMenuItem>
            {statuses.map(status => (
              <DropdownMenuItem key={status} onClick={() => setStatusFilter(status)}>
                {statusLabels[status] || status}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Table - Desktop */}
      <div className="hidden lg:block bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Booking Ref
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                  onClick={() => handleSort('customer_name')}
                >
                  <span className="flex items-center gap-1">
                    Lead Booker
                    <ArrowUpDown className="w-3 h-3" />
                  </span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Guests
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Room
                </th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                  onClick={() => handleSort('amount_paid')}
                >
                  <span className="flex items-center justify-end gap-1">
                    Paid
                    <ArrowUpDown className="w-3 h-3" />
                  </span>
                </th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                  onClick={() => handleSort('balance')}
                >
                  <span className="flex items-center justify-end gap-1">
                    Balance
                    <ArrowUpDown className="w-3 h-3" />
                  </span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="px-4 py-4">
                      <div className="h-10 bg-slate-100 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No bookings match your filters'
                      : 'No bookings yet'}
                  </td>
                </tr>
              ) : (
                filteredBookings.map((booking) => {
                  const balance = getBalance(booking);
                  
                  return (
                    <tr 
                      key={booking.booking_id}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => openBookingDrawer(booking)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-violet-600">
                          {booking.booking_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{booking.customer_name}</p>
                          <p className="text-sm text-slate-500">{booking.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Users className="w-4 h-4 text-slate-400" />
                          {getGuestCount(booking)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {getPrimaryRoom(booking)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-emerald-600">
                          {formatCurrency(booking.amount_paid)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn(
                          "font-medium",
                          balance > 0 ? "text-orange-600" : "text-slate-400"
                        )}>
                          {formatCurrency(balance)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={cn("text-xs", statusColors[booking.status])}>
                          {statusLabels[booking.status] || booking.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => openBookingDrawer(booking)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card Layout - Mobile */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="h-24 bg-slate-100 rounded animate-pulse" />
            </div>
          ))
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
            {searchQuery || statusFilter !== 'all' 
              ? 'No bookings match your filters'
              : 'No bookings yet'}
          </div>
        ) : (
          filteredBookings.map((booking) => {
            const balance = getBalance(booking);
            
            return (
              <div 
                key={booking.booking_id}
                className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-violet-300 transition-colors"
                onClick={() => openBookingDrawer(booking)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="font-mono text-sm font-medium text-violet-600">
                      {booking.booking_id}
                    </span>
                    <p className="font-medium text-slate-900 mt-1">{booking.customer_name}</p>
                    <p className="text-sm text-slate-500">{booking.customer_email}</p>
                  </div>
                  <Badge className={cn("text-xs", statusColors[booking.status])}>
                    {statusLabels[booking.status] || booking.status}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {getGuestCount(booking)} guests
                    </span>
                    <span>{getPrimaryRoom(booking)}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium text-emerald-600">{formatCurrency(booking.amount_paid)}</span>
                    {balance > 0 && (
                      <span className="text-orange-600 ml-2">
                        ({formatCurrency(balance)} due)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Booking Detail Drawer */}
      <BookingDetailDrawer
        booking={selectedBooking}
        event={event}
        open={drawerOpen}
        onClose={closeBookingDrawer}
        authToken={authToken}
        onBookingUpdated={handleBookingUpdated}
      />

      {/* Manual Booking Dialog */}
      <ManualWeekenderBookingDialog
        event={event}
        open={manualBookingOpen}
        onOpenChange={setManualBookingOpen}
        authToken={authToken}
        onBookingCreated={(newBooking) => {
          // Refresh bookings list after creation
          if (onBookingChange) onBookingChange();
          if (onRefresh) onRefresh();
        }}
      />
    </div>
  );
}
